import type { To } from 'react-router';
import { AddIcon, DropdownTriangle } from '#assets/ReactSvg.ts';
import { Button } from '#components/atoms/buttons/Button.tsx';
import { LinkButton } from '#components/atoms/buttons/link-button/LinkButton.tsx';
import { Link } from '#components/atoms/link/Link.tsx';
import { Tooltip } from '#components/atoms/tooltip/Tooltip.tsx';
import { ButtonGroup, ButtonGroupItem } from '#components/patterns/button-group/ButtonGroup.tsx';
import { Menu, MenuButton, MenuItem, useMenuStore } from '#components/patterns/menus/menu/index.ts';
import { useAppLocation } from '#hooks/useAppLocation.ts';
import styles from './AddSplitButton.module.css';

export type AddMenuItem = {
  label: string;
  to: To;
};

type Props = {
  /** Destination of the primary (link) half. */
  addTo: To;
  /** Accessible + visible label of the primary half. */
  addLabel: string;
  /** Secondary add destinations, shown in the caret half's menu. */
  menuItems: AddMenuItem[];
  /** Icon-only rendering for the collapsed (stacked-header) HeaderButtons variant. When collapsed,
   * this renders two sibling {@link ButtonGroupItem}s so the halves join the caller's stretched
   * group instead of nesting a second group inside one item. */
  collapsed?: boolean;
};

/**
 * {@link AddSplitButton} is a split "Add" control: the primary half is a plain link (so it stays
 * reachable as `role=link` for assistive tech and tests), the caret half opens a menu of the other
 * add destinations.
 */
export function AddSplitButton({ addTo, addLabel, menuItems, collapsed }: Props) {
  const location = useAppLocation();
  const menu = useMenuStore();

  const caretHalf = (
    <>
      <MenuButton
        store={menu}
        aria-label="More add options"
        render={
          <Button size="small" variant={collapsed ? 'icon' : 'primary'} className={styles.AddSplitButton_caret} />
        }
      >
        <DropdownTriangle height={6} width={10} aria-hidden={true} />
      </MenuButton>
      <Menu store={menu} className="menu-list" portal={true} unmountOnHide={true} gutter={4}>
        {menuItems.map((item) => (
          <MenuItem key={item.label} render={<Link to={item.to} state={{ from: location }} />}>
            {item.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );

  if (collapsed) {
    return (
      <>
        <ButtonGroupItem className="width-100">
          <Tooltip
            content={addLabel}
            placement="bottom"
            render={<LinkButton size="small" variant="icon" to={addTo} state={{ from: location }} />}
          >
            <span className="visually-hidden">{addLabel}</span>
            <AddIcon aria-hidden={true} />
          </Tooltip>
        </ButtonGroupItem>
        <ButtonGroupItem compact={true}>{caretHalf}</ButtonGroupItem>
      </>
    );
  }

  return (
    <ButtonGroup>
      <ButtonGroupItem>
        <LinkButton size="small" to={addTo} state={{ from: location }}>
          {addLabel}
        </LinkButton>
      </ButtonGroupItem>
      <ButtonGroupItem>{caretHalf}</ButtonGroupItem>
    </ButtonGroup>
  );
}
